import React from "react";
import AppChip from "../../../shared/components/ui/AppChip";
import { SPOKEN_LANGUAGE_OPTIONS } from "../../../shared/data/profileTaxonomy";
import { useOnboarding } from "../OnboardingContext";
import OnboardingWizard from "../OnboardingWizard";
import { useEditStepSave } from "../useEditStepSave";

const DEFAULT_LANGUAGE_STARS = 4;
const BIO_MIN_CHARS = 80;
const BIO_MAX_CHARS = 500;

function LanguageStarPicker({ level, onChange }) {
  return (
    <div className="onboarding-language-stars" role="group" aria-label="Proficiency">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={`onboarding-language-star${
            star <= level ? " onboarding-language-star--filled" : ""
          }`}
          aria-label={`${star} star${star === 1 ? "" : "s"}`}
          aria-pressed={star <= level}
          onClick={() => onChange(star)}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function toggleLanguage(languages, languageName) {
  const exists = languages.some((entry) => entry.name === languageName);
  if (exists) {
    return languages.filter((entry) => entry.name !== languageName);
  }
  return [...languages, { name: languageName, stars: DEFAULT_LANGUAGE_STARS }];
}

function setLanguageStars(languages, languageName, stars) {
  return languages.map((entry) => (
    entry.name === languageName ? { ...entry, stars } : entry
  ));
}

export default function StepProfileInfo() {
  const { draft, updateDraft, goToNextStep, goToPrevStep, isEditMode } = useOnboarding();

  const selectedNames = draft.languages.map((entry) => entry.name);
  const bioLength = draft.desc.trim().length;
  const canContinue = bioLength >= BIO_MIN_CHARS && draft.languages.length > 0;
  const bioReady = bioLength >= BIO_MIN_CHARS;
  const { showSave, onSave, saveDisabled, saveLabel, saveError } = useEditStepSave();

  return (
    <OnboardingWizard
      hideChrome
      stepKey="info"
      title="Profile information"
      subtitle={isEditMode
        ? "Update your bio and language proficiency."
        : "Write a short bio and select the languages you speak."}
      onBack={() => goToPrevStep("info")}
      onContinue={() => goToNextStep("info")}
      continueDisabled={!canContinue}
      showSave={showSave}
      onSave={onSave}
      saveDisabled={saveDisabled}
      saveLabel={saveLabel}
      contentClassName="onboarding-wizard__content--info"
    >
      {saveError ? (
        <p className="onboarding-error-banner" role="alert">
          {saveError}
        </p>
      ) : null}

      <div className="onboarding-field onboarding-field--bio">
        <label className="onboarding-field__label" htmlFor="onboarding-bio">
          Short bio
        </label>
        <div className="onboarding-bio-editor">
          <textarea
            id="onboarding-bio"
            className="onboarding-field__textarea onboarding-field__textarea--bio onboarding-bio-editor__textarea"
            value={draft.desc}
            onChange={(e) => updateDraft({ desc: e.target.value })}
            placeholder="A warm, discreet introduction…"
            maxLength={BIO_MAX_CHARS}
            rows={8}
          />
        </div>
        <p className={`onboarding-field__hint onboarding-field__hint--bio${bioReady ? " onboarding-field__hint--ready" : ""}`}>
          {bioLength} / {BIO_MAX_CHARS}
          {!bioReady ? ` · at least ${BIO_MIN_CHARS} characters` : ""}
        </p>
      </div>

      <div className="onboarding-field">
        <span className="onboarding-field__label">Languages spoken</span>
        <div className="onboarding-chip-grid">
          {SPOKEN_LANGUAGE_OPTIONS.map((language) => (
            <AppChip
              key={language}
              label={language}
              selected={selectedNames.includes(language)}
              onClick={() => updateDraft({
                languages: toggleLanguage(draft.languages, language),
              })}
            />
          ))}
        </div>
      </div>

      {draft.languages.length > 0 ? (
        <div className="onboarding-language-list">
          {draft.languages.map((language) => (
            <div key={language.name} className="onboarding-language-row">
              <span className="onboarding-language-row__name">{language.name}</span>
              <LanguageStarPicker
                level={language.stars ?? DEFAULT_LANGUAGE_STARS}
                onChange={(stars) => updateDraft({
                  languages: setLanguageStars(draft.languages, language.name, stars),
                })}
              />
            </div>
          ))}
        </div>
      ) : null}
    </OnboardingWizard>
  );
}
